// campaign-schedule.ts — WHEN a campaign/sequence sends, resolved from the real
// scheduling fields rather than from `startedAt`.
//
// Why this file exists: the sequence detail derived every send time from
// `drip.startedAt`, which `launchDrip` stamps at LAUNCH time — NOT at the
// scheduled send time. A campaign launched today and scheduled for next week
// therefore had a base of "now" (or, for a synthesized standalone campaign,
// nothing at all), so the detail rendered "Sends when ready" and "Started —"
// while the actual due date — sitting right there in `schedule.dueDate` on both
// the drip and the step campaign — was shown nowhere on the page.
//
// Everything here is PURE (dates in, plain data out) so it is unit-testable
// without a DOM: the jest env is `node`, and the detail is a component file.

import type { DripStatus } from '@bt-common/schemas/bt/drip';

/** The date shapes the drip/campaign documents actually carry: a real `Date`
 *  from a Mongoose read, an ISO string over the wire, or nothing. */
export type RawDate = string | Date | null | undefined;

/** Parse a document date defensively — an absent, empty or unparseable value is
 *  `undefined`, never an `Invalid Date` that would render as "Invalid Date". */
export const toValidDate = (raw: RawDate): Date | undefined => {
  if (raw === null || raw === undefined || raw === '') return undefined;
  const d = raw instanceof Date ? new Date(raw.getTime()) : new Date(raw);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

// Locale-aware date+time. The old `date-fns format(d, 'MMM d, h:mm a')` hard-coded
// US month-first order and a 12-hour clock for a market that reads day-first on a
// 24-hour clock. Same pattern as campaigns-redesign/launched.tsx. Shared by the
// campaign page and its Audience/When tabs so one campaign reads one way.
export const formatDateTime = (d: Date): string =>
  d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** WHERE the base send time came from. The distinction is load-bearing: a
 *  `*-schedule` source is the time the user actually chose (a fact we may state),
 *  `started` is only the launch stamp (which for a future-dated campaign is in
 *  the past and says nothing about when it sends). */
export type SendBaseSource = 'campaign-schedule' | 'drip-schedule' | 'started' | 'none';

export interface SendBase {
  at?: Date;
  source: SendBaseSource;
}

/** True when the base is a time the USER scheduled, not merely the launch stamp. */
export const isScheduledSource = (source: SendBaseSource): boolean =>
  source === 'campaign-schedule' || source === 'drip-schedule';

/** The base wall-clock time step 0 sends at — the anchor the later steps' gaps
 *  are walked forward from.
 *
 *  Order: the STEP CAMPAIGN's own `schedule.dueDate` first (the most specific and
 *  the value the dispatcher actually fires on — `createStepCampaign` fixes it at
 *  creation), then the DRIP's `schedule.dueDate` (set before any step campaign
 *  exists), and only then `startedAt` (the launch stamp — the legacy behaviour,
 *  kept as the last resort so an old drip with no schedule still reads as it did). */
export const resolveBaseSendAt = (input: {
  campaignDueDate?: RawDate;
  dripDueDate?: RawDate;
  startedAt?: RawDate;
}): SendBase => {
  const fromCampaign = toValidDate(input.campaignDueDate);
  if (fromCampaign) return { at: fromCampaign, source: 'campaign-schedule' };
  const fromDrip = toValidDate(input.dripDueDate);
  if (fromDrip) return { at: fromDrip, source: 'drip-schedule' };
  const fromStart = toValidDate(input.startedAt);
  if (fromStart) return { at: fromStart, source: 'started' };
  return { source: 'none' };
};

// ─── Header timing — "Scheduled for X" vs "Started X" ───

/** What the detail header's third subtitle slot states. `none` keeps the legacy
 *  em-dash placeholder (a draft with no schedule genuinely has no date). */
export type HeaderTiming =
  | { kind: 'started'; at: Date }
  | { kind: 'scheduled'; at: Date }
  | { kind: 'none' };

/** `Started —` is useless for a campaign that hasn't sent yet — it's the single
 *  question the customer asked ("I can't see at all the date/time that was set").
 *  Until anything has actually dispatched, state the SCHEDULE; once it has,
 *  `Started` is the truthful label again.
 *
 *  `hasDispatched` = any step is in-flight or done (the detail derives it from
 *  the existing `stepStateFromCampaign` mapping, so this stays in lock-step with
 *  the timeline nodes). A dispatched sequence with no `startedAt` (a synthesized
 *  standalone campaign carries none) falls back to its due date, still labelled
 *  `Started` — it did start, and that is when. */
export const resolveHeaderTiming = (input: {
  base: SendBase;
  startedAt?: RawDate;
  hasDispatched: boolean;
}): HeaderTiming => {
  const started = toValidDate(input.startedAt);
  const scheduled = isScheduledSource(input.base.source) ? input.base.at : undefined;
  if (input.hasDispatched) {
    const at = started ?? scheduled ?? input.base.at;
    return at ? { kind: 'started', at } : { kind: 'none' };
  }
  if (scheduled) return { kind: 'scheduled', at: scheduled };
  return started ? { kind: 'started', at: started } : { kind: 'none' };
};

// ─── Header badge ───

/** The detail header's badge vocabulary: the drip statuses plus `pending`, which
 *  the shared STATUS_MAP already renders as "Scheduled". */
export type DisplayDripStatus = DripStatus | 'pending';

/** A launched-but-not-yet-sending drip stores `status: 'active'`, which the badge
 *  rendered as "Running" — a lie for a campaign due next week, and a
 *  contradiction of the list card (which reads a standalone campaign's own
 *  `pending` status and correctly says "Scheduled").
 *
 *  This is deliberately a DISPLAY-ONLY narrowing layered ON TOP of
 *  `deriveDripStatus`, not a change to it: that selector's `DripStatus` result
 *  also feeds `useDripActions` (Pause/Resume gating) and the list card, and
 *  widening it would ripple through both. `paused`/`completed`/`aborted`/`draft`
 *  are already honest and pass through untouched. */
export const deriveDisplayStatus = (
  effective: DripStatus,
  hasDispatched: boolean,
): DisplayDripStatus => (effective === 'active' && !hasDispatched ? 'pending' : effective);

// ─── Scheduled-step copy ───

/** How much we trust a step's send time. `exact` = it IS the stored due date the
 *  dispatcher fires on. `estimate` = walked forward from the configured gaps,
 *  which assume each prior step completes instantly. */
export type SendPrecision = 'exact' | 'estimate';

/** Below this, show a live per-second countdown instead of a coarse distance. */
export const COUNTDOWN_WINDOW_MS = 60_000;

/** A render plan for the scheduled-step card. Pure data — the component supplies
 *  the date-fns formatting (and the live `now`), so every branch is assertable
 *  without a DOM. */
export interface SendTimingDisplay {
  /** `relative` → `headline` is a PREFIX and the component appends the distance
   *  to `at`. `countdown`/`fixed` → `headline` is the whole line. */
  kind: 'countdown' | 'relative' | 'fixed';
  headline: string;
  /** The absolute time to render (the relative anchor and/or the sub-line). */
  at?: Date;
  /** Muted sub-line prefix. Absent ⇒ no sub-line at all. */
  subLead?: string;
}

/** The scheduled card's wording.
 *
 *  The rule this encodes: never print a stale estimate as fact. An `exact` time
 *  may be stated ("Sends … at Aug 10, 5:43 PM"); a walked-forward one is hedged
 *  ("Expected … around …"); and an `estimate` whose moment has already passed
 *  says nothing at all, falling back to the honest "Sends when ready". An
 *  `exact` time that has passed without dispatching is real information — the
 *  send is overdue — so it reads "Sending shortly · was due <date>". */
export const describeSendTiming = (
  sendAt: Date | undefined,
  precision: SendPrecision,
  now: number,
): SendTimingDisplay => {
  const ms = sendAt ? sendAt.getTime() - now : Number.NaN;
  if (!sendAt || Number.isNaN(ms)) return { kind: 'fixed', headline: 'Sends when ready' };

  if (ms <= 0) {
    return precision === 'exact'
      ? { kind: 'fixed', headline: 'Sending shortly', at: sendAt, subLead: 'was due' }
      : { kind: 'fixed', headline: 'Sends when ready' };
  }
  if (ms <= COUNTDOWN_WINDOW_MS) {
    // Imminent either way, so the headline doesn't hedge — but the absolute
    // sub-line still keeps the precision-appropriate preposition.
    return {
      kind: 'countdown',
      headline: `Sends in ${Math.ceil(ms / 1000)}s`,
      at: sendAt,
      subLead: precision === 'exact' ? 'at' : 'around',
    };
  }
  return precision === 'exact'
    ? { kind: 'relative', headline: 'Sends', at: sendAt, subLead: 'at' }
    : { kind: 'relative', headline: 'Expected', at: sendAt, subLead: 'around' };
};

// ─── Follow-up step send-time projection (BE#313) ───

/** A drip step's inter-message gap unit (mirrors DripStepGap.unit / RecurrenceUnit). */
export type StepGapUnit = 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
export interface StepGap { value: number; unit: StepGapUnit }

/** gap → milliseconds. month/year use calendar-average lengths (30.4375d / 365.25d) —
 *  a projection, never a stored fire time; follow-ups are minute/hour/day in practice, which
 *  are exact. Kept self-contained (no date-fns / no @bt-common component import) so the
 *  calendar selector stays a light pure dependency. */
const gapToMs = (gap?: StepGap | null): number => {
  if (!gap || typeof gap.value !== 'number' || gap.value <= 0) return 0;
  const MIN = 60_000, HOUR = 60 * MIN, DAY = 24 * HOUR;
  switch (gap.unit) {
    case 'minute': return gap.value * MIN;
    case 'hour': return gap.value * HOUR;
    case 'day': return gap.value * DAY;
    case 'week': return gap.value * 7 * DAY;
    case 'month': return Math.round(gap.value * 30.4375 * DAY);
    case 'year': return Math.round(gap.value * 365.25 * DAY);
    default: return 0;
  }
};

export interface ProjectedStep { stepIndex: number; at: Date }

/**
 * BE#313 — project the wall-clock send time of each drip step from `baseAt` (the anchor: the
 * CURRENT step's scheduled send time, i.e. the materialized step-campaign's `schedule.dueDate`).
 * Step `fromIndex` sends at `baseAt`; each later step sends its own step's `gapAfter` after the one
 * before it — the same forward walk sequence-detail's `derived` uses, so the calendar and the
 * sequence view agree. Returns one entry per step from `fromIndex` to the end (inclusive of the
 * anchor step, so callers can skip it and keep only the future steps).
 */
export function projectFollowupSendDates(
  baseAt: Date | undefined,
  steps: ReadonlyArray<{ stepIndex: number; gapAfter?: StepGap | null }>,
  fromIndex: number,
): ProjectedStep[] {
  if (!baseAt || Number.isNaN(baseAt.getTime()) || !Array.isArray(steps)) return [];
  const out: ProjectedStep[] = [];
  let runningMs = baseAt.getTime();
  for (let i = Math.max(0, fromIndex); i < steps.length; i++) {
    out.push({ stepIndex: steps[i].stepIndex, at: new Date(runningMs) });
    runningMs += gapToMs(steps[i].gapAfter); // advance by THIS step's own gapAfter for the next
  }
  return out;
}

// ─── Calendar / list "upcoming send" derivations ───

/**
 * FE#341-display — should a campaign/step occurrence show on the calendar as an UPCOMING
 * send? The rule EXCLUDES only what we can PROVE is already sent: a non-pending occurrence
 * whose send moment is concretely in the PAST. Everything else shows —
 *   - `pending` (queued, hasn't sent) → show, always;
 *   - a FUTURE send moment → show;
 *   - an UNKNOWN/undecidable date → show (we must never hide an occurrence we cannot prove
 *     is already-sent — hiding a future drip's step whose campaign carried no own dueDate
 *     was the FE#325 regression).
 * Pure. Applies to a drip's base step AND each projected follow-up step alike (pass that
 * occurrence's own send-time ms), so future steps always render while past steps drop out.
 */
export function isUpcomingCampaignSend(
  campaignStatus: string | undefined,
  dueDateMs: number | undefined,
  nowMs: number,
): boolean {
  // Pending (queued) and paused (intentionally held — not sent) always show; a paused campaign
  // must appear on the calendar (dimmed) regardless of a past due date, so the user sees it sits
  // in their schedule. Everything else: undecidable date → show; else only if still in the future.
  if (campaignStatus === 'pending' || campaignStatus === 'paused') return true;
  // Undecidable date → show (never hide without proof it already sent).
  if (dueDateMs === undefined || Number.isNaN(dueDateMs)) return true;
  return dueDateMs >= nowMs;
}

/**
 * FE#341-display — the DATE a drip's list card should show: the next NOT-YET-SENT step's
 * send time, not the launch/created date. Anchors the forward walk on the current
 * materialized step's scheduled send (falling back to the drip schedule / startedAt) and
 * returns the first projected step at or after `now`. Returns undefined when there is no
 * upcoming send (a fully-sent / un-anchorable drip) so the caller can fall back.
 */
export function nextUpcomingDripSendAt(
  drip: { steps?: ReadonlyArray<{ stepIndex: number; gapAfter?: StepGap | null }>; currentStep?: number; schedule?: { dueDate?: RawDate }; startedAt?: RawDate } | undefined,
  stepCampaigns: ReadonlyArray<{ dripStepIndex?: number; schedule?: { dueDate?: RawDate } }>,
  now: Date,
): Date | undefined {
  const steps = drip?.steps;
  if (!Array.isArray(steps) || steps.length === 0) return undefined;
  const currentIndex = typeof drip?.currentStep === 'number' ? drip.currentStep : 0;
  const currentCampaign = stepCampaigns.find((c) => c.dripStepIndex === currentIndex);
  const base = resolveBaseSendAt({
    campaignDueDate: currentCampaign?.schedule?.dueDate,
    dripDueDate: drip?.schedule?.dueDate,
    startedAt: drip?.startedAt,
  });
  if (!base.at) return undefined;
  const nowMs = now.getTime();
  const upcoming = projectFollowupSendDates(base.at, steps, currentIndex).find((p) => p.at.getTime() >= nowMs);
  return upcoming?.at;
}

export interface DripCalOccurrence { stepIndex: number; at: Date; isCurrent: boolean }

/**
 * FE#341 — project a drip's UPCOMING step occurrences for the calendar, FROM THE DRIP DOC
 * ALONE. The campaigns service HIDES drip-step campaigns from any find that does not name
 * `dripId` (drip-step-scope.ts), so a future scheduled drip's step campaign is usually NOT
 * in the store — and the calendar, which iterated campaigns, rendered nothing for it while
 * the list (which reads drip docs) showed it fine. This derives the occurrences the same way
 * the list does: anchor on the drip's own schedule/startedAt (or, for a running drip whose
 * current step campaign IS loaded, that step's real dueDate), walk the gaps from the current
 * step, and keep only steps that are still upcoming (pending/future) — an already-sent (past)
 * step drops out. Pure.
 */
export function projectDripCalendarOccurrences(
  drip: { steps?: ReadonlyArray<{ stepIndex: number; gapAfter?: StepGap | null }>; currentStep?: number; schedule?: { dueDate?: RawDate }; startedAt?: RawDate } | undefined,
  now: Date,
  currentStepDueDate?: RawDate,
): DripCalOccurrence[] {
  const steps = drip?.steps;
  if (!Array.isArray(steps) || steps.length === 0) return [];
  const currentIndex = typeof drip?.currentStep === 'number' ? drip.currentStep : 0;
  const base = resolveBaseSendAt({
    campaignDueDate: currentStepDueDate,
    dripDueDate: drip?.schedule?.dueDate,
    startedAt: drip?.startedAt,
  });
  if (!base.at) return [];
  const nowMs = now.getTime();
  return projectFollowupSendDates(base.at, steps, currentIndex)
    // undefined status → date-only rule: a FUTURE step shows, a concretely PAST step drops.
    .filter((p) => isUpcomingCampaignSend(undefined, p.at.getTime(), nowMs))
    .map((p) => ({ stepIndex: p.stepIndex, at: p.at, isCurrent: p.stepIndex === currentIndex }));
}

// ─── List DATE column semantics (FE#340) ────────────────────────────────────
//
// The management-list "Date" column showed ONE timestamp per row derived from a
// mixed fallback chain (`schedule.dueDate ?? createdAt` for a campaign; the
// upcoming-send-or-`startedAt` chain for a drip). Under a single generic "Date"
// header that meant a COMPLETED campaign showed its *scheduled* (or, worse, its
// *created*) time instead of when it actually SENT — "not connected to reality"
// (the customer's words). A pending row and a sent row read identically.
//
// This resolver makes the value AND its meaning explicit and status-aware, so the
// row (and the renamed header) can name exactly what each date is:
//   • completed            → `sent`      (when it actually went out)
//   • a concrete upcoming send, or a not-yet-dispatched schedule → `scheduled`
//   • dispatched, in-flight, nothing upcoming → `started`
//   • draft                → `created`
// Pure (dates in, tagged value out) so it is unit-testable without a DOM.

export type ListDateKind = 'sent' | 'scheduled' | 'started' | 'created' | 'none';

export interface ListDateCell {
  kind: ListDateKind;
  /** The chosen timestamp, or undefined when the row has no meaningful date. */
  at?: Date;
}

/**
 * Resolve WHICH timestamp a management-list row shows and WHAT it means.
 *
 * @param status        the row's lifecycle status (`completed`/`draft` are the only
 *                      values read explicitly; everything else is decided by
 *                      `hasDispatched` + `upcomingSendAt`, so the wider
 *                      running/active/paused/aborted/expired vocabulary needs no
 *                      per-value branch here).
 * @param hasDispatched did anything actually send yet (list derives it from `sent`).
 * @param upcomingSendAt a FUTURE scheduled send already resolved by the caller
 *                      (a pending campaign's due date, or a running drip's next
 *                      not-yet-sent step) — when present it is the most useful thing
 *                      to show and always reads as `scheduled`.
 * @param startedAt     launch / first-dispatch stamp — the real "sent" anchor.
 * @param scheduledAt   the configured due date (may be in the past).
 * @param completedAt   best "finished" fallback for a sent time (list passes `updatedAt`).
 * @param createdAt     creation time — last-resort fallback / draft date.
 */
export const resolveListDate = (input: {
  status: string;
  hasDispatched: boolean;
  upcomingSendAt?: RawDate;
  startedAt?: RawDate;
  scheduledAt?: RawDate;
  completedAt?: RawDate;
  createdAt?: RawDate;
}): ListDateCell => {
  const upcoming = toValidDate(input.upcomingSendAt);
  const started = toValidDate(input.startedAt);
  const scheduled = toValidDate(input.scheduledAt);
  const completed = toValidDate(input.completedAt);
  const created = toValidDate(input.createdAt);

  // Terminal: show when it actually SENT, never the scheduled/created time.
  if (input.status === 'completed') {
    const at = started ?? scheduled ?? completed ?? created;
    return at ? { kind: 'sent', at } : { kind: 'none' };
  }

  // A concrete upcoming send is the single most useful thing to surface, whatever
  // the status (pending campaign, or a running drip's next step).
  if (upcoming) return { kind: 'scheduled', at: upcoming };

  if (input.status === 'draft') {
    return created ? { kind: 'created', at: created } : { kind: 'none' };
  }

  // Not dispatched yet and no upcoming send resolved: fall back to the configured
  // schedule (a past due date still reads as "Scheduled" — it was), then created.
  if (!input.hasDispatched) {
    if (scheduled) return { kind: 'scheduled', at: scheduled };
    return created ? { kind: 'created', at: created } : { kind: 'none' };
  }

  // Dispatched, in-flight, nothing upcoming → when it STARTED.
  const at = started ?? scheduled ?? completed ?? created;
  return at ? { kind: 'started', at } : { kind: 'none' };
};
